/**
 * Agent — the entity that owns a conversation (or a sub-conversation).
 *
 * FVC 172-spine. New entity kind, additive.
 *
 * VibeChat's main agent (vc-main) is one Agent. Any spawned subagents
 * (fes = freshvibe-edit-suggest, fer = freshvibe-edit-revise, audit,
 * other) are also Agents. An Agent is the actor that produces Turns.
 *
 * Pattern parity with Conversation.ts / Turn.ts:
 *   - `id` (UUID/ULID) + `fcid: fcid://agent/<id>` canonical reference
 *   - factory + type-guard + version field for forward-only evolution
 *
 * Linkage:
 *   - spawnedByTurn → `turn` kind (the turn that spawned this agent;
 *     absent for the main agent)
 *   - spawnedInThread → existing `thread` kind (the thread this agent
 *     was spawned in; matches Conversation's project field convention)
 *
 * liveness: "live" agents are currently runnable; "archived" agents
 * are preserved for forensics but not re-instantiated.
 */

export type AgentKind = 'vc-main' | 'fes' | 'fer' | 'audit' | 'other';
export type AgentLiveness = 'live' | 'archived';

export interface Agent {
  id: string;
  fcid: string;
  project: string;
  /** Human-readable, themed name (e.g. "Tulip", "Bramble", "Sage") */
  label: string;
  kind: AgentKind;
  liveness: AgentLiveness;
  /** Whether the agent accepts live input (false = batch / autonomous only) */
  interactive: boolean;
  /** Turn that spawned this agent; null for the main agent */
  spawnedByTurn: string | null;
  /** Thread the agent was spawned in; null for the main agent */
  spawnedInThread: string | null;
  /** Optional themed naming rotation marker */
  dailyTheme: string | null;
  /** Optional sandbox reference (path or fcid; NOT a copy) */
  sandbox: string | null;

  /** Schema version for forward-only evolution (FvW §43) */
  version: 1;
  createdAt: string;
  updatedAt: string;
}

export function createAgent(input: {
  label: string;
  project: string;
  kind?: AgentKind;
  liveness?: AgentLiveness;
  interactive?: boolean;
  spawnedByTurn?: string | null;
  spawnedInThread?: string | null;
  dailyTheme?: string | null;
  sandbox?: string | null;
  id?: string;
}): Agent {
  const now = new Date().toISOString();
  const id = input.id ?? generateAgentId();
  return {
    id,
    fcid: `fcid://agent/${id}`,
    project: input.project,
    label: input.label,
    kind: input.kind ?? 'other',
    liveness: input.liveness ?? 'live',
    interactive: input.interactive ?? true,
    spawnedByTurn: input.spawnedByTurn ?? null,
    spawnedInThread: input.spawnedInThread ?? null,
    dailyTheme: input.dailyTheme ?? null,
    sandbox: input.sandbox ?? null,
    version: 1,
    createdAt: now,
    updatedAt: now,
  };
}

export function generateAgentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `agent-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isAgent(value: unknown): value is Agent {
  if (!value || typeof value !== 'object') return false;
  const a = value as Record<string, unknown>;
  return (
    typeof a.id === 'string' &&
    typeof a.label === 'string' &&
    typeof a.project === 'string' &&
    typeof a.fcid === 'string' &&
    a.fcid.startsWith('fcid://agent/') &&
    (a.kind === 'vc-main' || a.kind === 'fes' || a.kind === 'fer' || a.kind === 'audit' || a.kind === 'other') &&
    (a.liveness === 'live' || a.liveness === 'archived') &&
    typeof a.interactive === 'boolean' &&
    a.version === 1
  );
}
